import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import useUsers2Data from '@/hooks/useUsers2Data'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import EditButton from '@/components/EditButton'
import DeleteButton from '@/components/DeleteButton'
import { getSortIndicator } from '@/lib/tableHelpers'

const getUserSortValue = (user, column) => {
	if (column === 'name') {
		return user?.nazwa || user?.name || ''
	}

	if (column === 'email') {
		return user?.email || ''
	}

	if (column === 'alternative_name') {
		return user?.alternative_name || ''
	}

	if (column === 'roles') {
		const roles = []
		if (user?.isAdmin) roles.push('admin')
		if (user?.isCoordinator) roles.push('coordinator')
		if (user?.isTrainer) roles.push('trainer')
		return roles.sort().join(',')
	}

	return ''
}

const sortUsers = (users, sortColumn, sortDirection) => {
	const direction = sortDirection === 'asc' ? 1 : -1

	return [...users].sort((a, b) => {
		const firstValue = getUserSortValue(a, sortColumn)
		const secondValue = getUserSortValue(b, sortColumn)
		return String(firstValue).localeCompare(String(secondValue), 'pl') * direction
	})
}

const Uzytkownicy = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const {
		data,
		loading,
		error,
		clearError,
		loadData,
		remove,
		removing,
	} = useUsers2Data()

	const [sortColumn, setSortColumn] = useState('name')
	const [sortDirection, setSortDirection] = useState('asc')

	const sortedData = useMemo(
		() => sortUsers(data, sortColumn, sortDirection),
		[data, sortColumn, sortDirection]
	)

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSortColumn(column)
		setSortDirection(newDirection)
	}

	const renderSortableHeader = (column, label) => (
		<button
			type="button"
			className="flex items-center gap-1 font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
			onClick={() => handleSort(column)}
		>
			{label}
			{getSortIndicator(column, sortColumn, sortDirection)}
		</button>
	)

	const renderRoles = user => {
		const roles = [
			{ key: 'admin', active: user.isAdmin, label: t('settingsPage.users.roles.admin') },
			{ key: 'trainer', active: user.isTrainer, label: t('settingsPage.users.roles.trainer') },
			{ key: 'coordinator', active: user.isCoordinator, label: t('settingsPage.users.roles.coordinator') },
		].filter(role => role.active)

		if (!roles.length) return <span>-</span>

		return (
			<div className="flex flex-wrap gap-1">
				{roles.map(role => (
					<span key={role.key} className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
						{role.label}
					</span>
				))}
			</div>
		)
	}

	const getUserDisplayName = user => {
		const name = user?.nazwa || user?.name || '-'
		const alternativeName = user?.alternative_name || user?.initials

		return alternativeName ? `${name} (${alternativeName})` : name
	}

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	if (loading) {
		return <LoadingIndicator message={t('settingsPage.users.loading')} />
	}

	const handleRemove = async (id, name) => {
		const result = await Swal.fire({
			title: t('notifications.confirmTitle'),
			text: t('settingsPage.users.messages.removeConfirm', { name }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('notifications.confirmDeleteButton'),
			cancelButtonText: t('notifications.cancelButton'),
		})

		if (result.isConfirmed) {
			const removeResult = await remove(id)
			if (removeResult) {
				Swal.fire({
					title: t('notifications.successTitle'),
					text: t('settingsPage.users.messages.removedSuccess', { name }),
					icon: 'success',
				})
			}
		}
	}

	return (
		<div className="rounded-lg bg-white shadow-sm">
			<div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
				<h1 className="text-xl font-semibold text-gray-900">{t('sidebar.items.uzytkownicy')}</h1>
				<Link
					to="/system/uzytkownicy/add"
					className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
				>
					+ {t('settingsPage.users.actions.addUser')}
				</Link>
			</div>

			<div className="overflow-x-auto p-6">
			<div className="overflow-x-auto rounded-lg border border-gray-200">

				<table className="w-full text-sm text-left shadow-sm text-gray-500">
					<thead className="border-b border-gray-200 bg-gray-50">
						<tr>
							<th scope="col" className="px-6 py-3">{renderSortableHeader('name', t('settingsPage.users.table.name'))}</th>
							<th scope="col" className="px-6 py-3">{renderSortableHeader('email', t('settingsPage.users.table.email'))}</th>
							<th scope="col" className="px-6 py-3">{renderSortableHeader('alternative_name', t('settingsPage.users.table.alternativeName'))}</th>
							<th scope="col" className="px-6 py-3">{renderSortableHeader('roles', t('settingsPage.users.table.roles'))}</th>
							<th scope="col" className="px-6 py-3 text-right">{t('settingsPage.users.table.actions')}</th>
						</tr>
					</thead>
					<tbody>
						{sortedData.map(user => (
							<tr key={user._id} className="bg-white hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
								<td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
									{user.nazwa || '-'}
								</td>
								<td className="px-6 py-4">
									{user.email || '-'}
								</td>
								<td className="px-6 py-4">
									{user.alternative_name || '-'}
								</td>
								<td className="px-6 py-4">
									{renderRoles(user)}
								</td>
								<td className="px-6 py-4 text-right">
									<div className="flex justify-end space-x-2">
										<EditButton
											onClick={() => navigate(`/system/uzytkownicy/edit/${user._id}`)}
											title={t('settingsPage.users.actions.updateTitle')}
											label={t('settingsPage.users.actions.edit')}
										/>
										<DeleteButton
											onClick={() => handleRemove(user._id, getUserDisplayName(user))}
											disabled={removing}
											title={t('settingsPage.users.actions.deleteTitle')}
											label={t('settingsPage.users.actions.delete')}
										/>
									</div>
								</td>
							</tr>
						))}
						{sortedData.length === 0 && (
							<tr>
								<td colSpan={5} className="px-6 py-4 text-center text-gray-600">
									{t('settingsPage.users.empty')}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			</div>
		</div>
	)
}

export default Uzytkownicy
